// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import axios from 'axios'; // promise-based HTTP client to make asynchronous HTTP requests >  allows you to intercept requests or responses with common headers (e.g., authentication tokens).
import { ENV } from '../ENV';

import { authContextRef } from '../../web-utils/src/context/authContextRef';
import { store } from '../store/store';

import { logout } from '../store/globalActions';

import {
  storeTokens,
  setToken,
  setRefreshToken,
  getRefreshToken,
} from '../../web-utils/src/utils/storageUtils';

// ------------------------
// AXIOS INSTANCE (DEFAULT PORT)
// ------------------------
export const axiosInstance = axios.create({
  baseURL: ENV.REACT_APP_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  config => {
    const authContext = authContextRef.current;
    const token = authContext?.accessToken;

    // Only set Authorization if not already set (prevents overwriting refreshed token)
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = token;
    }

    return config;
  },
  error => Promise.reject(error)
);

// Refresh token handling
let isRefreshing = false;
let pendingRequests = [];

const resolvePendingRequests = (error, newAccessToken) => {
  pendingRequests.forEach(({ resolve, reject }) =>
    error ? reject(error) : resolve(newAccessToken)
  );
  pendingRequests = [];
};

const handleLogout = () => {
  authContextRef.current?.logout();
  axiosInstance.defaults.headers.common.Authorization = null;
  pendingRequests = [];
  store.dispatch(logout(true));
};

// Response interceptor
axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Queue requests while refresh is in progress
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject });
        }).then(newAccessToken => {
          originalRequest.headers.Authorization = newAccessToken;
          return axiosInstance.request(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        const authContext = authContextRef.current;
        const accessToken = authContext?.token;
        const refreshToken = getRefreshToken();

        if (!accessToken || !refreshToken) {
          throw new Error('Missing credentials for refresh');
        }

        const response = await axios.post(
          `${ENV.REACT_APP_API_URL}refresh-token/`,
          {
            refresh_token: refreshToken,
            access_token: accessToken,
          },
          {
            headers: {
              Authorization: accessToken, // expired token allowed
            },
          }
        );
        const {
          access_token: newAccessToken,
          token_type: type,
          expires_in: expiresIn,
          refresh_token: newRefreshToken,
        } = response.data;

        const tokenType = type
          ? type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
          : 'Bearer';

        const newAuthHeader = `${tokenType} ${newAccessToken}`;

        // Update cookies and context
        storeTokens(newAccessToken, tokenType, expiresIn);
        setToken(newAccessToken);
        setRefreshToken(newRefreshToken);
        authContextRef.current?.login(newAuthHeader, newAccessToken);

        //  Update default axios header
        axiosInstance.defaults.headers.common.Authorization = newAuthHeader;

        // Resolve queued requests
        resolvePendingRequests(null, newAuthHeader);

        // Retry original request
        originalRequest.headers.Authorization = newAuthHeader;
        return axiosInstance.request(originalRequest);
      } catch (refreshError) {
        resolvePendingRequests(refreshError, null);
        handleLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
